// v4
import { LoadingOutlined, UploadOutlined } from "@ant-design/icons";
import {
    Button,
    Card,
    Col,
    Input,
    Row,
    Select,
    Upload,
    Typography,
    Tooltip,
} from "antd";
import Checkbox from "antd/lib/checkbox/Checkbox";
import { Dispatch, FC, SetStateAction, useEffect, useState } from "react";

import { ICountry, IModal } from "@types";
import CustomModal from "@/components/customModel";
import {
    ErrorServices,
    useFetchAllCompanyJobGradesQuery,
    useFetchAllCompanyJobFunctionsQuery,
    useCreateBenefitsPlanMutation,
    useUpdateBenefitsPlanMutation,
    useFetchEmployeeTypeRangesQuery,
} from "@services";
import {
    showSuccessPopup,
    sortedByName,
} from "@utils";
import { useTypedSelector } from "@hooks";
import { getTokenFromSharedStorage } from "@/services/cookies";
import customErrorMessage from "@/components/CustomErrorMessage";
import { formatNumericTwoDecimalInputWithCommas } from "@/utils/formatNumberWithCommas";

const { Option } = Select;
const { Text } = Typography;

const SELECT_ALL_VALUE = "__SELECT_ALL__";
const CLEAR_ALL_VALUE = "__CLEAR_ALL__";
interface IBenefitPlanModalProps extends IModal {
    selectedBenefitPlan?: any | null;
    setSelectedBenefitPlan?: Dispatch<SetStateAction<any | null>>;
}

interface IBenefitPlanForm {
    name: string;
    category: string | null;

    business_unit_ids: number[];
    job_function_id: number | null;
    sub_job_function_ids: number[];

    employee_type_categories: string[];

    grade: Array<string | number>;

    country_id: number[];
    city: string;

    benefit_entitlement: string;
    calculation_basis: string | null;

    benefit_value_or_cte: number | null;
    benefit_limit: number | null;

    do_not_show_in_offer: boolean;
    custom_detail_in_offer: boolean;
    offer_custom_detail: string;

    description: string;

    policy_manual: File | null;
}

const INITIAL_BENEFITS_PLAN: IBenefitPlanForm = {
    name: "",
    category: null,

    business_unit_ids: [],
    job_function_id: null,
    sub_job_function_ids: [],

    employee_type_categories: [],

    grade: [],

    country_id: [],
    city: "",

    benefit_entitlement: "",
    calculation_basis: null,

    benefit_value_or_cte: null,
    benefit_limit: null,

    do_not_show_in_offer: false,
    custom_detail_in_offer: false,
    offer_custom_detail: "",

    description: "",

    policy_manual: null,
};

const BenefitPlanModal: FC<IBenefitPlanModalProps> = ({
    isVisible,
    setIsVisible,
    selectedBenefitPlan = null,
    setSelectedBenefitPlan,
}) => {
    const { company } = useTypedSelector(state => state.auth.user!);
    const { id: company_id } = company!;
    const [loading, setLoading] = useState(false);

    const [benefitPlan, setBenefitPlan] = useState<any>(INITIAL_BENEFITS_PLAN);

    const isEditMode = Boolean(selectedBenefitPlan?.id);

    const [createBenefitPlan, { isLoading: isCreating }] =
        useCreateBenefitsPlanMutation();
    const [updateBenefitPlan, { isLoading: isUpdating }] =
        useUpdateBenefitsPlanMutation();

    const [gradeCountries, setGradeCountries] = useState<ICountry[]>([]);

    const { data: jobGradesData, isLoading: isLoadingJobGrades } =
        useFetchAllCompanyJobGradesQuery({
            company_id,
        });

    const jobGrades = jobGradesData?.data || [];

    const {
        data: employeeTypeRangesData,
        isLoading: isLoadingEmployeeTypeRanges,
    } = useFetchEmployeeTypeRangesQuery(
        { company_id },
        { skip: !company_id }
    );

    const employeeTypeRanges = Array.isArray(employeeTypeRangesData?.data)
        ? employeeTypeRangesData.data
        : [];

    const employeeTypeCategoryOptions = Array.from(
        new Set(
            employeeTypeRanges
                .map((item: any) => item?.employee_type_category)
                .filter(Boolean)
        )
    ) as string[];

    const selectedEmployeeTypeCategories = Array.isArray(
        benefitPlan?.employee_type_categories
    )
        ? benefitPlan.employee_type_categories.filter(Boolean)
        : [];

    // Employee-type grade/country flow must only activate after an
    // employee type category has actually been selected.
    const isEmployeeTypeFlow = selectedEmployeeTypeCategories.length > 0;

    const selectedEmployeeTypeRanges = isEmployeeTypeFlow
        ? employeeTypeRanges.filter((item: any) =>
            selectedEmployeeTypeCategories.includes(
                item?.employee_type_category
            )
        )
        : [];

    const employeeTypeGradeOptions = Array.from(
        selectedEmployeeTypeRanges
            .flatMap((item: any) =>
                (item?.countries || []).flatMap(
                    (country: any) => country?.grades || []
                )
            )
            .reduce((gradeMap: Map<number, any>, grade: any) => {
                if (grade?.id == null) return gradeMap;
                gradeMap.set(grade.id, grade);
                return gradeMap;
            }, new Map<number, any>())
            .values()
    );

    const employeeTypeCountryOptions = Array.from(
        selectedEmployeeTypeRanges
            .flatMap((item: any) => item?.countries || [])
            .reduce((countryMap: Map<number, any>, country: any) => {
                if (!country?.id) return countryMap;

                const existingCountry = countryMap.get(country.id);
                const mergedGrades = Array.from(
                    [
                        ...(existingCountry?.grades || []),
                        ...(country?.grades || []),
                    ]
                        .reduce((gradeMap: Map<number, any>, grade: any) => {
                            if (grade?.id == null) return gradeMap;
                            gradeMap.set(grade.id, grade);
                            return gradeMap;
                        }, new Map<number, any>())
                        .values()
                );

                countryMap.set(country.id, {
                    ...existingCountry,
                    ...country,
                    grades: mergedGrades,
                });

                return countryMap;
            }, new Map<number, any>())
            .values()
    );

    const employeeTypeAvailableCountries = employeeTypeCountryOptions.filter(
        (country: any) =>
            benefitPlan?.grade?.length === 0 ||
            country?.grades?.some((grade: any) =>
                benefitPlan.grade.includes(grade?.id)
            )
    );

    const originalJobGradeOptions = Array.from(
        new Set(
            jobGrades
                .map((jobGrade: any) => jobGrade?.grade)
                .filter(Boolean)
        )
    ) as string[];

    const {
        data: companyJobFunctionsData,
        isLoading: isLoadingCompanyJobFunctions,
    } = useFetchAllCompanyJobFunctionsQuery({
        companyId: company_id,
    });

    const companyJobFunctions = Array.isArray(companyJobFunctionsData?.data)
        ? companyJobFunctionsData.data
        : companyJobFunctionsData?.data?.results || [];

    const selectedCompanyJobFunction =
        companyJobFunctions.find(
            (jobFunction: any) =>
                jobFunction.id === benefitPlan?.job_function_id
        );

    const companySubJobFunctions =
        selectedCompanyJobFunction?.job_sub_functions ||
        selectedCompanyJobFunction?.sub_job_functions ||
        [];

    const isEmployeeTypeDisabled =
        benefitPlan?.job_function_id ||
        benefitPlan?.sub_job_function_ids?.length > 0;

    const isJobFunctionDisabled =
        Array.isArray(benefitPlan?.employee_type_categories) &&
        benefitPlan.employee_type_categories.length > 0;

    useEffect(() => {
        if (!isVisible) return;

        if (!isEditMode) {
            setBenefitPlan(INITIAL_BENEFITS_PLAN);
            return;
        }

        if (isLoadingEmployeeTypeRanges || isLoadingJobGrades) return;

        const selectedGradeObjects = selectedBenefitPlan?.grade_obj || [];

        const countryIds =
            selectedBenefitPlan?.countries
                ?.map(
                    (country: any) =>
                        country?.id ?? country?.country_id
                )
                ?.filter(
                    (id: any) => id !== null && id !== undefined
                ) || [];

        const businessUnitIds = (
            selectedBenefitPlan?.business_units || []
        )
            .map((businessUnit: any) => businessUnit.id)
            .filter(Boolean);

        const subJobFunctionIds = (
            selectedBenefitPlan?.sub_job_function ||
            selectedBenefitPlan?.sub_job_functions ||
            []
        )
            .map(
                (subJobFunction: any) =>
                    subJobFunction?.id ??
                    subJobFunction?.sub_job_function_id
            )
            .filter(
                (id: any) => id !== null && id !== undefined
            );

        const jobFunctionIds = (
            selectedBenefitPlan?.job_function ||
            selectedBenefitPlan?.job_functions ||
            []
        )
            .map(
                (jobFunction: any) =>
                    jobFunction?.id ?? jobFunction?.job_function_id
            )
            .filter(
                (id: any) => id !== null && id !== undefined
            );

        const jobFunctionId =
            jobFunctionIds.length > 0
                ? jobFunctionIds[0]
                : selectedBenefitPlan?.job_function_id ?? null;

        const selectedEmployeeTypeCategories =
            jobFunctionId || subJobFunctionIds.length > 0
                ? []
                : Array.isArray(
                    selectedBenefitPlan?.employee_type_categories
                )
                    ? selectedBenefitPlan.employee_type_categories
                    : selectedBenefitPlan?.employee_type_category
                        ? [selectedBenefitPlan.employee_type_category]
                        : [];

        const selectedGrades = selectedGradeObjects
            .map((grade: any) => {
                if (selectedEmployeeTypeCategories.length > 0) {
                    return grade?.id;
                }

                return grade?.grade || grade?.name;
            })
            .filter((grade: any) => grade !== null && grade !== undefined);

        const formattedBenefitPlan: IBenefitPlanForm = {
            name: selectedBenefitPlan?.name || "",
            category: selectedBenefitPlan?.category || null,

            business_unit_ids: businessUnitIds,
            job_function_id: jobFunctionId,
            sub_job_function_ids: subJobFunctionIds,

            employee_type_categories: selectedEmployeeTypeCategories,

            grade: selectedGrades,

            country_id: countryIds,
            city: selectedBenefitPlan?.city || "",

            benefit_entitlement:
                selectedBenefitPlan?.benefit_entitlement || "",

            calculation_basis:
                selectedBenefitPlan?.calculation_basis || null,

            benefit_value_or_cte:
                selectedBenefitPlan?.benefit_value_or_cte != null
                    ? Number(selectedBenefitPlan.benefit_value_or_cte)
                    : null,

            benefit_limit:
                selectedBenefitPlan?.benefit_limit != null
                    ? Number(selectedBenefitPlan.benefit_limit)
                    : null,

            do_not_show_in_offer:
                selectedBenefitPlan?.exclude_from_offer ?? false,

            custom_detail_in_offer:
                selectedBenefitPlan?.show_custom_detail ?? false,

            offer_custom_detail:
                selectedBenefitPlan?.custom_detail ?? "",

            description: selectedBenefitPlan?.description || "",

            policy_manual: null,
        };

        console.log("Selected Benefit", selectedBenefitPlan);
        console.log("Grades", selectedBenefitPlan?.grades);
        console.log("Selected Grades", selectedGrades);

        if (selectedEmployeeTypeCategories.length === 0) {
            const selectedJobGrades = jobGrades.filter((jobGrade: any) =>
                selectedGrades.includes(jobGrade?.grade)
            );

            const mergedCountries = selectedJobGrades.flatMap(
                (jobGrade: any) => jobGrade?.countries || []
            );

            const uniqueCountries = mergedCountries.filter(
                (country: any, index: number, self: any[]) =>
                    country?.id &&
                    index === self.findIndex(item => item?.id === country?.id)
            );

            setGradeCountries(uniqueCountries);
        } else {
            setGradeCountries([]);
        }

        setBenefitPlan(formattedBenefitPlan);
    }, [
        isVisible,
        isEditMode,
        selectedBenefitPlan,
        employeeTypeRangesData?.data,
        jobGradesData?.data,
        isLoadingEmployeeTypeRanges,
        isLoadingJobGrades,
    ]);

    // Handle input & checkbox changes
    const handleChange = (e: any) => {
        const { name, value, checked, type } = e.target;

        let updatedValue: any =
            type === "checkbox" ? checked : value;

        // Cost to Employer:
        // keep as string while typing so decimals work
        if (name === "benefit_value_or_cte") {
            const cleanedValue = String(value).replace(/,/g, "");

            // Allow empty value
            if (cleanedValue === "") {
                updatedValue = "";
            } else {
                // Only numbers + optional decimal + max 2 decimal digits
                if (!/^\d+(\.\d{0,2})?$/.test(cleanedValue)) {
                    return;
                }

                updatedValue = cleanedValue;
            }
        }

        // Keep existing Benefit Limit behavior
        if (name === "benefit_limit") {
            updatedValue =
                value === ""
                    ? null
                    : Number(String(value).replace(/,/g, ""));
        }

        setBenefitPlan((prev: any) => ({
            ...prev,
            [name]: updatedValue,
        }));
    };

    // Handle Select changes
    const handleSelectChange = (name: string, value: any) => {
        if (name === "job_function_id") {
            setBenefitPlan((prev: any) => ({
                ...prev,
                job_function_id: value,
                sub_job_function_ids: [],
                employee_type_categories: [],
                grade: [],
                country_id: [],
            }));
            return;
        }

        if (name === "sub_job_function_ids") {
            setBenefitPlan((prev: any) => ({
                ...prev,
                sub_job_function_ids: value,
                employee_type_categories: [],
                grade: [],
                country_id: [],
            }));
            return;
        }

        if (name === "employee_type_categories") {
            setBenefitPlan((prev: any) => ({
                ...prev,
                employee_type_categories: value,
                job_function_id: null,
                sub_job_function_ids: [],
                grade: [],
                country_id: [],
            }));
            return;
        }

        setBenefitPlan((prev: any) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleMultiSelectChange = (
        name: string,
        values: any[],
        allValues: any[],
        onChange?: (nextValues: any[]) => void
    ) => {
        let nextValues = values || [];

        if (nextValues.includes(CLEAR_ALL_VALUE)) {
            nextValues = [];
        } else if (nextValues.includes(SELECT_ALL_VALUE)) {
            nextValues = allValues;
        } else {
            nextValues = nextValues.filter(
                value => value !== SELECT_ALL_VALUE && value !== CLEAR_ALL_VALUE
            );
        }

        if (onChange) {
            onChange(nextValues);
            return;
        }

        handleSelectChange(name, nextValues);
    };

    const getCountriesByGrades = (selectedGrades: string[]) => {
        const selectedJobGrades = jobGrades.filter((jobGrade: any) =>
            selectedGrades.includes(jobGrade?.grade)
        );

        const mergedCountries = selectedJobGrades.flatMap(
            (jobGrade: any) => jobGrade?.countries || []
        );

        return mergedCountries.filter(
            (country: any, index: number, self: any[]) =>
                country?.id &&
                index === self.findIndex(item => item?.id === country?.id)
        );
    };

    const handleJobGradeChange = (selectedGrades: string[]) => {
        const availableCountries = getCountriesByGrades(selectedGrades);

        setGradeCountries(availableCountries);
        setBenefitPlan((prev: any) => ({
            ...prev,
            grade: selectedGrades,
            country_id: [],
        }));
    };

    const handleEmployeeTypeGradeChange = (selectedGradeIds: number[]) => {
        const validCountryIds = employeeTypeCountryOptions
            .filter((country: any) =>
                country?.grades?.some((grade: any) =>
                    selectedGradeIds.includes(grade?.id)
                )
            )
            .map((country: any) => country.id);

        setBenefitPlan((prev: any) => ({
            ...prev,
            grade: selectedGradeIds,
            country_id: (prev?.country_id || []).filter(
                (countryId: number) => validCountryIds.includes(countryId)
            ),
        }));
    };

    const handleGradeChange = (selectedGrades: Array<string | number>) => {
        if (isEmployeeTypeFlow) {
            handleEmployeeTypeGradeChange(selectedGrades as number[]);
            return;
        }

        handleJobGradeChange(selectedGrades as string[]);
    };


    const uploadPolicy = async (): Promise<boolean> => {
        if (!benefitPlan?.policy_manual) return false; // no file = allow submit
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append("policy_manual", benefitPlan.policy_manual);

            const res = await fetch(
                `${process.env.REACT_APP_BASE_URL}/company/${company_id}/upload-policy/`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${getTokenFromSharedStorage()?.access}`,
                        Accept: "application/json",
                    },
                    body: formData,
                }
            );
            setLoading(false);
            if (!res.ok) {
                throw new Error("Upload failed");
            }

            const data = await res.json();
            return data;
        } catch (error) {
            setLoading(false);
            return false;
        }
    };

    // Upload handler
    const handleUpload = (file: any) => {
        setBenefitPlan(prev => ({ ...prev, policy_manual: file }));
        return false; // prevent auto upload
    };

    // Validation
    const isValid = () => {
        const hasGrades =
            Array.isArray(benefitPlan?.grade) && benefitPlan.grade.length > 0;

        const hasCountry =
            Array.isArray(benefitPlan?.country_id) &&
            benefitPlan.country_id.length > 0;

        const hasCustomDetail =
            !benefitPlan?.custom_detail_in_offer ||
            !!benefitPlan?.offer_custom_detail;

        const hasCompanyAssignment =
            !!benefitPlan?.job_function_id ||
            (Array.isArray(benefitPlan?.sub_job_function_ids) &&
                benefitPlan.sub_job_function_ids.length > 0);

        const hasEmployeeTypeAssignment =
            Array.isArray(benefitPlan?.employee_type_categories) &&
            benefitPlan.employee_type_categories.length > 0;

        const hasAssignment =
            hasCompanyAssignment || hasEmployeeTypeAssignment;

        return (
            !!benefitPlan?.name?.trim() &&
            !!benefitPlan?.category &&
            hasGrades &&
            hasCountry &&
            !!benefitPlan?.benefit_entitlement?.trim() &&
            !!benefitPlan?.calculation_basis &&
            benefitPlan?.benefit_value_or_cte !== null &&
            benefitPlan?.benefit_value_or_cte !== undefined &&
            benefitPlan?.benefit_value_or_cte !== "" &&
            hasAssignment &&
            hasCustomDetail
        );
    };

    const handleSubmit = async () => {
        try {
            // 1️⃣ Upload policy first
            const uploadedPolicy = await uploadPolicy();
            // if (
            //   !selectedBenefitPlan?.policy_manual_url &&
            //   !benefitPlan?.policy_manual
            // ) {
            //   customErrorMessage("Policy is required. Benefit plan not updated.");
            //   return;
            // }
            const payload = {
                name: benefitPlan?.name,

                category: benefitPlan?.category,

                employee_type_categories:
                    benefitPlan?.job_function_id ||
                        benefitPlan?.sub_job_function_ids?.length > 0
                        ? []
                        : benefitPlan?.employee_type_categories || [],

                benefit_entitlement:
                    benefitPlan?.benefit_entitlement || "",

                calculation_basis:
                    benefitPlan?.calculation_basis || null,

                benefit_value_or_cte:
                    benefitPlan?.benefit_value_or_cte !== null &&
                        benefitPlan?.benefit_value_or_cte !== undefined &&
                        benefitPlan?.benefit_value_or_cte !== ""
                        ? Number(benefitPlan.benefit_value_or_cte)
                        : null,

                benefit_limit:
                    benefitPlan?.benefit_limit !== null &&
                        benefitPlan?.benefit_limit !== undefined &&
                        benefitPlan?.benefit_limit !== ""
                        ? Number(benefitPlan.benefit_limit)
                        : null,

                exclude_from_offer:
                    benefitPlan?.do_not_show_in_offer,

                show_custom_detail:
                    benefitPlan?.custom_detail_in_offer,

                description:
                    benefitPlan?.description,

                country_ids:
                    benefitPlan?.country_id || [],

                city:
                    benefitPlan?.city,

                grade_ids: isEmployeeTypeFlow
                    ? benefitPlan?.grade || []
                    : jobGrades
                        .filter((jobGrade: any) =>
                            benefitPlan?.grade?.includes(jobGrade.grade)
                        )
                        .map((jobGrade: any) => jobGrade.id),

                business_unit_ids:
                    benefitPlan?.business_unit_ids || [],

                custom_detail:
                    benefitPlan?.custom_detail_in_offer
                        ? benefitPlan?.offer_custom_detail
                        : null,

                job_function_id:
                    benefitPlan?.job_function_id,

                sub_job_function_ids:
                    benefitPlan?.sub_job_function_ids?.length
                        ? benefitPlan?.sub_job_function_ids
                        : null,

                // NEW
                policy_manual_url:
                    uploadedPolicy?.data?.policy_manual_url ??
                    selectedBenefitPlan?.policy_manual_url ??
                    null,
            };

            if (isEditMode) {
                await updateBenefitPlan({
                    company_id,
                    id: selectedBenefitPlan?.id,
                    body: payload,
                }).unwrap();

                showSuccessPopup({
                    title: "Benefit Plan Updated",
                    desc: "You have successfully updated the benefit plan.",
                    role: "client",
                });
            } else {
                await createBenefitPlan({
                    company_id,
                    body: payload,
                }).unwrap();

                showSuccessPopup({
                    title: "Benefit Plan Created",
                    desc: "You have successfully created the benefit plan.",
                    role: "client",
                });
            }

            setBenefitPlan(INITIAL_BENEFITS_PLAN);
            setSelectedBenefitPlan?.(null);
            setIsVisible(false);
            setLoading(false);
        } catch (error) {
            setLoading(false);
            ErrorServices(error);
        }
    };


    const hasCompanyAssignment =
        !!benefitPlan?.job_function_id ||
        (Array.isArray(benefitPlan?.sub_job_function_ids) &&
            benefitPlan.sub_job_function_ids.length > 0);

    const hasEmployeeTypeAssignment =
        Array.isArray(benefitPlan?.employee_type_categories) &&
        benefitPlan.employee_type_categories.length > 0;

    const missingFields = [
        !benefitPlan?.name?.trim() && "Name of Benefit Plan",

        !hasCompanyAssignment &&
        !hasEmployeeTypeAssignment &&
        "Function / Sub Function or Employee Type Category",

        (!Array.isArray(benefitPlan?.grade) ||
            benefitPlan.grade.length === 0) &&
        "Grades",

        (!Array.isArray(benefitPlan?.country_id) ||
            benefitPlan.country_id.length === 0) &&
        "Country",

        !benefitPlan?.category && "Benefit Category",

        !benefitPlan?.benefit_entitlement?.trim() &&
        "Benefit Entitlement",

        !benefitPlan?.calculation_basis &&
        "Calculation Basis",

        (benefitPlan?.benefit_value_or_cte === null ||
            benefitPlan?.benefit_value_or_cte === undefined ||
            benefitPlan?.benefit_value_or_cte === "") &&
        "Benefit Value / Cost-to-Employer",

        benefitPlan?.custom_detail_in_offer &&
        !benefitPlan?.offer_custom_detail?.trim() &&
        "Custom Offer Detail",
    ].filter(Boolean);

    const disabledReason =
        missingFields.length > 0
            ? `Please fill in: ${missingFields.join(", ")}`
            : isCreating || isUpdating || loading
                ? "Please wait..."
                : "";

    return (
        <CustomModal
            width="w-7/12"
            title={isEditMode ? "Update Benefit Plan" : "Create Benefit Plan"}
            isVisible={isVisible}
            height="80vh"
            footer={
                <>
                    <button
                        className="bg-revampOrange text-white font-medium text-lg py-2 px-14 rounded-md"
                        onClick={() => {
                            setBenefitPlan(INITIAL_BENEFITS_PLAN);
                            setSelectedBenefitPlan?.(null);
                            setIsVisible(false);
                        }}
                    >
                        {" "}
                        <span> Cancel</span>
                    </button>
                    <Tooltip title={disabledReason}>
                        <span>
                            <button
                                className={`bg-revampBlue text-white font-medium text-lg py-2 px-14 rounded-md ${!isValid() || isCreating || isUpdating || loading
                                    ? "opacity-50 cursor-not-allowed"
                                    : ""
                                    }`}
                                disabled={
                                    !isValid() ||
                                    isCreating ||
                                    isUpdating ||
                                    loading
                                }
                                onClick={handleSubmit}
                            >
                                <span>
                                    {isCreating || isUpdating || loading ? (
                                        <LoadingOutlined />
                                    ) : isEditMode ? (
                                        "Update"
                                    ) : (
                                        "Create"
                                    )}
                                </span>
                            </button>
                        </span>
                    </Tooltip>
                </>
            }
            onClose={() => {
                setBenefitPlan(INITIAL_BENEFITS_PLAN);
                setSelectedBenefitPlan?.(null);
                setIsVisible(false);
            }}
        >
            <Row gutter={16}>
                <Col span={12}>
                    <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
                        <span className="font-medium text-revampBlue text-lg">
                            Name of Benefit Plan <span className="text-red-500">*</span>
                        </span>
                    </label>
                    <Input
                        className={`borderless-input w-full shadow-sm  font-semibold text-sm bg-white px-3 py-3 rounded-md`}
                        size="large"
                        name="name"
                        placeholder="Name of Benefit Plan"
                        value={benefitPlan?.name}
                        onChange={handleChange}
                    />
                </Col>
                {/* <Col span={12}>
          <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
            Business Unit
          </label>

          <Select
            className="custom-select"
            size="large"
            mode="multiple"
            maxTagCount="responsive"
            maxTagPlaceholder={omittedValues =>
              `+${omittedValues.length} more`
            }
            placeholder="Select Business Units"
            value={benefitPlan?.business_unit_ids || undefined}
            onChange={values =>
              handleSelectChange(
                "business_unit_ids",
                values
              )
            }
          >
            {businessUnits.map((bu: any) => (
              <Option key={bu.id} value={bu.id}>
                {bu.name}
              </Option>
            ))}
          </Select>
        </Col> */}
            </Row>

            <Row gutter={16} style={{ marginTop: 16 }}>
                <Col span={12}>
                    <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
                        <span className="font-medium text-revampBlue text-lg">
                            Function
                            {!(isJobFunctionDisabled) && <span className="text-red-500 ml-1">*</span>}
                        </span>
                    </label>

                    <Select
                        className="custom-select"
                        size="large"
                        showSearch
                        optionFilterProp="children"
                        loading={isLoadingCompanyJobFunctions}
                        allowClear
                        placeholder="Select Function"
                        value={benefitPlan?.job_function_id || undefined}
                        disabled={isJobFunctionDisabled}
                        onChange={value =>
                            handleSelectChange(
                                "job_function_id",
                                value
                            )
                        }
                    >
                        {sortedByName(companyJobFunctions)?.map(
                            (item: any) => (
                                <Option
                                    key={item.id}
                                    value={item.id}
                                >
                                    {item.name}
                                </Option>
                            )
                        )}
                    </Select>
                </Col>

                <Col span={12}>
                    <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">

                        <span className="font-medium text-revampBlue text-lg">
                            Sub Function
                            {!(isJobFunctionDisabled) && <span className="text-red-500 ml-1">*</span>}
                        </span>

                    </label>

                    <Select
                        className="custom-select"
                        size="large"
                        mode="multiple"
                        showSearch
                        optionFilterProp="children"
                        maxTagCount="responsive"
                        maxTagPlaceholder={o =>
                            `+${o.length} more`
                        }
                        disabled={
                            !benefitPlan?.job_function_id
                        }
                        placeholder="Select Sub Function"
                        value={
                            benefitPlan?.sub_job_function_ids ||
                            undefined
                        }
                        onChange={values =>
                            handleMultiSelectChange(
                                "sub_job_function_ids",
                                values,
                                companySubJobFunctions.map((item: any) => item.id)
                            )
                        }
                    >
                        <Option value={SELECT_ALL_VALUE}>All Sub Functions</Option>
                        {/* <Option value={CLEAR_ALL_VALUE}>Clear All</Option> */}
                        {sortedByName(
                            companySubJobFunctions
                        )?.map((item: any) => (
                            <Option
                                key={item.id}
                                value={item.id}
                            >
                                {item.name}
                            </Option>
                        ))}
                    </Select>
                </Col>
            </Row>


            <>
                <Row gutter={16} style={{ marginTop: 16 }}>
                    <Col span={12}>
                        <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
                            <span className="font-medium text-revampBlue text-lg">
                                Employee Type Category
                                {!(isEmployeeTypeDisabled) && <span className="text-red-500 ml-1">*</span>}
                            </span>
                        </label>

                        <Select
                            className="custom-select"
                            size="large"
                            mode="multiple"
                            showSearch
                            optionFilterProp="children"
                            maxTagCount="responsive"
                            maxTagPlaceholder={omittedValues =>
                                `+${omittedValues.length} more`
                            }
                            loading={isLoadingEmployeeTypeRanges}
                            disabled={
                                isLoadingEmployeeTypeRanges ||
                                isEmployeeTypeDisabled
                            }
                            placeholder="Select Employee Type Categories"
                            value={
                                selectedEmployeeTypeCategories.length
                                    ? selectedEmployeeTypeCategories
                                    : undefined
                            }
                            onChange={values =>
                                handleMultiSelectChange(
                                    "employee_type_categories",
                                    values,
                                    employeeTypeCategoryOptions
                                )
                            }
                        >
                            <Option value={SELECT_ALL_VALUE}>All Categories</Option>
                            {employeeTypeCategoryOptions.map(category => (
                                <Option key={category} value={category}>
                                    {category}
                                </Option>
                            ))}
                        </Select>
                    </Col>

                    <Col span={12}>
                        <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
                            <span className="font-medium text-revampBlue text-lg">
                                Grades <span className="text-red-500">*</span>
                            </span>
                        </label>

                        {isEmployeeTypeFlow ? (
                            <Select
                                className="custom-select"
                                size="large"
                                mode="multiple"
                                maxTagCount="responsive"
                                maxTagPlaceholder={omittedValues =>
                                    `+${omittedValues.length} more`
                                }
                                showSearch
                                optionFilterProp="children"
                                loading={isLoadingEmployeeTypeRanges}
                                disabled={
                                    isLoadingEmployeeTypeRanges ||
                                    employeeTypeGradeOptions.length === 0
                                }
                                placeholder="Select Employee Type Grades"
                                value={
                                    benefitPlan?.grade?.length
                                        ? benefitPlan.grade
                                        : undefined
                                }
                                onChange={values =>
                                    handleMultiSelectChange(
                                        "grade",
                                        values,
                                        employeeTypeGradeOptions.map(
                                            (grade: any) => grade.id
                                        ),
                                        handleEmployeeTypeGradeChange
                                    )
                                }
                            >
                                <Option value={SELECT_ALL_VALUE}>All Grades</Option>
                                {employeeTypeGradeOptions.map((grade: any) => (
                                    <Option key={grade.id} value={grade.id}>
                                        {grade.name}
                                    </Option>
                                ))}
                            </Select>
                        ) : (
                            <Select
                                className="custom-select"
                                size="large"
                                mode="multiple"
                                maxTagCount="responsive"
                                maxTagPlaceholder={omittedValues =>
                                    `+${omittedValues.length} more`
                                }
                                showSearch
                                optionFilterProp="children"
                                loading={isLoadingJobGrades}
                                disabled={
                                    isLoadingJobGrades ||
                                    originalJobGradeOptions.length === 0
                                }
                                placeholder="Select Grades"
                                value={
                                    benefitPlan?.grade?.length
                                        ? benefitPlan.grade
                                        : undefined
                                }
                                onChange={values =>
                                    handleMultiSelectChange(
                                        "grade",
                                        values,
                                        originalJobGradeOptions,
                                        handleJobGradeChange
                                    )
                                }
                            >
                                <Option value={SELECT_ALL_VALUE}>All Grades</Option>
                                {originalJobGradeOptions.map((grade: string) => (
                                    <Option key={grade} value={grade}>
                                        {grade}
                                    </Option>
                                ))}
                            </Select>
                        )}

                        <Row style={{ marginTop: 16 }}></Row>
                    </Col>
                </Row>
            </>

            <Row gutter={16} style={{ marginTop: 16 }}>

                <Col span={12}>
                    <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
                        <span className="font-medium text-revampBlue text-lg">
                            Country <span className="text-red-500">*</span>
                        </span>
                    </label>

                    {isEmployeeTypeFlow ? (
                        <Select
                            className="custom-select"
                            size="large"
                            mode="multiple"
                            maxTagCount="responsive"
                            maxTagPlaceholder={omittedValues =>
                                `+${omittedValues.length} more`
                            }
                            loading={isLoadingEmployeeTypeRanges}
                            disabled={
                                isLoadingEmployeeTypeRanges ||
                                !benefitPlan?.grade?.length ||
                                employeeTypeAvailableCountries.length === 0
                            }
                            placeholder={
                                benefitPlan?.grade?.length
                                    ? "Select Employee Type Country"
                                    : "Select Grades First"
                            }
                            value={
                                benefitPlan?.country_id?.length
                                    ? benefitPlan.country_id
                                    : undefined
                            }
                            onChange={values =>
                                handleMultiSelectChange(
                                    "country_id",
                                    values,
                                    employeeTypeAvailableCountries.map(
                                        (country: any) => country.id
                                    )
                                )
                            }
                        >
                            <Option value={SELECT_ALL_VALUE}>All Countries</Option>
                            {sortedByName(employeeTypeAvailableCountries)?.map(
                                (country: any) => (
                                    <Option key={country.id} value={country.id}>
                                        {country.name}
                                    </Option>
                                )
                            )}
                        </Select>
                    ) : (
                        <Select
                            className="custom-select"
                            size="large"
                            mode="multiple"
                            maxTagCount="responsive"
                            maxTagPlaceholder={omittedValues =>
                                `+${omittedValues.length} more`
                            }
                            loading={isLoadingJobGrades}
                            disabled={
                                isLoadingJobGrades ||
                                !benefitPlan?.grade?.length ||
                                gradeCountries.length === 0
                            }
                            placeholder={
                                benefitPlan?.grade?.length
                                    ? "Select Country"
                                    : "Select Grades First"
                            }
                            value={
                                benefitPlan?.country_id?.length
                                    ? benefitPlan.country_id
                                    : undefined
                            }
                            onChange={values =>
                                handleMultiSelectChange(
                                    "country_id",
                                    values,
                                    gradeCountries.map(
                                        (country: any) => country.id
                                    )
                                )
                            }
                        >
                            <Option value={SELECT_ALL_VALUE}>All Countries</Option>
                            {sortedByName(gradeCountries)?.map(
                                (country: any) => (
                                    <Option key={country.id} value={country.id}>
                                        {country.name}
                                    </Option>
                                )
                            )}
                        </Select>
                    )}
                </Col>

                <Col span={12}>
                    <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
                        City
                    </label>
                    <Input
                        className={`borderless-input w-full shadow-sm  font-semibold text-sm bg-white px-3 py-3 rounded-md`}
                        size="large"
                        name="city"
                        placeholder="City"
                        value={benefitPlan?.city}
                        onChange={handleChange}
                    />
                </Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 16 }}>
                <Col span={12}>
                    <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
                        <span className="font-medium text-revampBlue text-lg">
                            Benefit Category <span className="text-red-500">*</span>
                        </span>
                    </label>

                    <Select
                        className="custom-select"
                        size="large"
                        allowClear
                        placeholder="Select Benefit Category"
                        value={benefitPlan?.category}
                        onChange={value =>
                            handleSelectChange("category", value)
                        }
                    >
                        <Option value="health">
                            Health
                        </Option>

                        <Option value="retirement">
                            Retirement
                        </Option>

                        <Option value="wellness">
                            Wellness
                        </Option>

                        <Option value="financial">
                            Financial
                        </Option>

                        <Option value="statutory">
                            Statutory
                        </Option>

                        <Option value="statutory_time_off">
                            Time Off
                        </Option>

                        {/* <Option value="lifestyle">
                            Lifestyle
                        </Option> */}


                        {/* <Option value="statutory_provisions">
                            Statutory – Provisions
                        </Option> */}

                        <Option value="others">
                            Others
                        </Option>
                    </Select>
                </Col>

                <Col span={12}>
                    <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
                        <span className="font-medium text-revampBlue text-lg">
                            Benefit Entitlement <span className="text-red-500">*</span>
                        </span>
                    </label>

                    <Input.TextArea
                        className="w-full resize shadow-sm font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md"
                        name="benefit_entitlement"
                        placeholder="Enter Benefit Entitlement"
                        value={benefitPlan?.benefit_entitlement}
                        onChange={handleChange}
                        autoSize={{
                            minRows: 3,
                            maxRows: 5,
                        }}
                    />
                </Col>
                {/* <Col span={14} className="mt-2">
          <Checkbox
            name="include_in_offer_detail"
            checked={benefitPlan?.include_in_offer_detail}
            onChange={handleChange}
          >
            Include in Offer Detail
          </Checkbox>

          <br />

          <Checkbox
            className="mt-1"
            name="include_in_total_remuneration"
            checked={benefitPlan?.include_in_total_remuneration}
            onChange={handleChange}
          >
            Include in Total Remuneration
          </Checkbox>

        </Col> */}
            </Row>


            <Row gutter={16} style={{ marginTop: 16 }}>
                <Col span={12}>
                    <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
                        <span className="font-medium text-revampBlue text-lg">
                            Calculation Basis <span className="text-red-500">*</span>
                        </span>
                    </label>

                    <Select
                        className="custom-select"
                        size="large"
                        allowClear
                        placeholder="Calculation Basis"
                        value={benefitPlan?.calculation_basis}
                        onChange={value =>
                            handleSelectChange(
                                "calculation_basis",
                                value
                            )
                        }
                    >
                        <Option value="percent_basic">
                            % of Annual Basic Salary
                        </Option>

                        <Option value="percent_guaranteed">
                            % of Annual Guaranteed Cash
                        </Option>
                        <Option value="multiple_of_weekly_basic">
                            Multiple of Weekly Basic Salary
                        </Option>

                        {/* <Option value="percent_actual">
              % of Actual
            </Option> */}

                        <Option value="flat">
                            Flat Amount
                        </Option>

                    </Select>
                </Col>

                <Col span={12}>
                    <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
                        <span className="font-medium text-revampBlue text-lg">
                            Benefit Value / Cost-to-Employer <span className="text-red-500">*</span>
                        </span>
                    </label>

                    <Input
                        // type="number"
                        className="borderless-input w-full shadow-sm  font-semibold text-sm bg-white px-3 py-3 rounded-md"
                        size="large"
                        name="benefit_value_or_cte"
                        placeholder="Benefit Value / Cost-to-Employer"
                        value={formatNumericTwoDecimalInputWithCommas(benefitPlan?.benefit_value_or_cte)}
                        onChange={handleChange}
                    />
                </Col>




            </Row>


            <Row gutter={16} style={{ marginTop: 16 }}>
                <Col span={12}>
                    <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
                        Benefit Limit
                    </label>

                    <Input
                        type="number"
                        className="borderless-input w-full shadow-sm font-semibold text-sm bg-white px-3 py-3 rounded-md"
                        size="large"
                        name="benefit_limit"
                        placeholder="Benefit Limit"
                        value={benefitPlan?.benefit_limit}
                        onChange={handleChange}
                    />
                </Col>
            </Row>

            <Row gutter={16} style={{ marginTop: 20 }}>
                <Col span={12}>
                    <div>
                        <Checkbox
                            name="do_not_show_in_offer"
                            checked={benefitPlan?.do_not_show_in_offer}
                            onChange={handleChange}
                        >
                            Do not show this Benefit in the Offer
                        </Checkbox>
                    </div>

                    <div style={{ marginTop: 12 }}>
                        <Checkbox
                            name="custom_detail_in_offer"
                            checked={benefitPlan?.custom_detail_in_offer}
                            onChange={handleChange}
                        >
                            Custom detail to show in Offer
                        </Checkbox>
                    </div>
                </Col>
            </Row>

            {
                benefitPlan?.custom_detail_in_offer && (
                    <Row gutter={16} style={{ marginTop: 16 }}>
                        <Col span={12}>
                            <label className="font-medium text-revampBlue text-lg leading-10 mt-4">

                                <span className="font-medium text-revampBlue text-lg">
                                    Custom Offer Detail <span className="text-red-500">*</span>
                                </span>
                            </label>

                            <Input.TextArea
                                className="w-full resize shadow-sm my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md"
                                name="offer_custom_detail"
                                placeholder="Enter the custom detail to display in the offer"
                                value={benefitPlan?.offer_custom_detail}
                                onChange={handleChange}
                            />

                            <Text type="secondary">
                                Benefit Value/Cost-to-Employer and Benefit Limit will not be displayed.
                            </Text>
                        </Col>
                    </Row>
                )
            }

            {/* <Row gutter={16} style={{ marginTop: 16 }}>
        <Col span={24}>
          <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
            Description
          </label>

          <Input.TextArea
            className="w-full resize shadow-sm my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md"
            name="description"
            placeholder="Describe the benefit plan..."
            value={benefitPlan?.description}
            onChange={handleChange}
          />
        </Col>
      </Row> */}

            <Row gutter={16} style={{ marginTop: 16 }}>
                <Col span={12}>
                    <>
                        {isEditMode && selectedBenefitPlan?.policy_manual_url && (
                            <Button className="mb-4" type="link">
                                <a
                                    href={selectedBenefitPlan?.policy_manual_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    View Existing Policy Manual
                                </a>
                            </Button>
                        )}

                        <Card bordered={false} style={{ background: "#fafafa" }}>
                            <Text strong>Policy Manual</Text>
                            <Text
                                type="secondary"
                                style={{ display: "block", marginBottom: 8 }}
                            >
                                Upload PDF or DOC/DOCX file
                            </Text>

                            <Upload
                                beforeUpload={file => {
                                    const fileExtension = file.name
                                        .split(".")
                                        .pop()
                                        ?.toLowerCase();
                                    const allowedExtensions = ["pdf", "doc", "docx"];

                                    if (
                                        !fileExtension ||
                                        !allowedExtensions?.includes(fileExtension)
                                    ) {
                                        customErrorMessage(
                                            "You can only upload PDF or DOC/DOCX files!"
                                        );
                                        return Upload.LIST_IGNORE;
                                    }

                                    handleUpload(file);
                                    return false; // stop auto upload but keep valid file
                                }}
                                onRemove={() => {
                                    setBenefitPlan(prev => ({ ...prev, policy_manual: null }));
                                }}
                                maxCount={1}
                            >
                                <button className="bg-themeBlue text-white font-medium text-lg py-1 px-6 rounded-md">
                                    <UploadOutlined /> Upload File
                                </button>
                            </Upload>
                        </Card>
                    </>
                </Col>
            </Row>
        </CustomModal>
    );
};

export default BenefitPlanModal;